import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import {
    Inbox,
    Send,
    FileText,
    AlertTriangle,
    Trash2,
    Archive,
    Flag,
    SquarePen,
    Plus,
    RefreshCw,
    ChevronRight,
    ChevronDown,
    Folder as FolderIcon,
    CalendarPlus,
    ListPlus,
    Search,
    X,
    type LucideIcon
} from "@fg/icons";
import { removeSavedSearch, useSavedSearches } from "@/lib/savedSearches";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useFolders, useCreateFolder, useDeleteFolder, useMarkAllRead, useEmptyTrash } from "@/api/hooks";
import type { Folder } from "@/api/mailApi";
import { ChatRoster } from "@/features/chat/ChatRoster";
import { TaskDropTargets } from "@/features/tasks/fromMessage";

// Folder `kind` is the stable identifier used to pick an icon locally, so the
// BFF never needs to know about icon libraries.
const iconByKind: Record<string, LucideIcon> = {
    inbox: Inbox,
    flagged: Flag,
    drafts: FileText,
    sent: Send,
    junk: AlertTriangle,
    archive: Archive,
    trash: Trash2
};

/** System folders pinned to the top of the pane, in this order (Outlook "Favourites"). */
const FAVOURITE_KINDS = ["inbox", "sent", "drafts", "flagged"] as const;

/** Section header — the same look for Favourites, the account, Tasks and Chats. */
export const SECTION_HEADER = "flex w-full min-w-0 items-center gap-1.5 px-2 pb-1 pt-2.5 text-xs font-semibold text-[var(--mail-text-2)]";

interface FolderPaneProps {
    selectedFolderId: string;
    onSelectFolder: (id: string) => void;
    onCompose: () => void;
    /** Optional — fires on hover/focus of a folder row so MailPage can
     *  TanStack-prefetch the messages for that folder before click. */
    onPrefetchFolder?: (folderId: string) => void;
    /** Drop handler — when a draggable MessageList row (or a batch of
     *  checked rows) is dropped on a folder, MailPage runs the move
     *  action. Source folder comes from the drag payload so same-folder
     *  drops can be ignored. ids is always non-empty. */
    onDropMessage?: (ids: string[], fromFolderId: string, toFolderId: string) => void;
    /** Open a saved search (its query becomes the current search). */
    onOpenSearch?: (query: string) => void;
    /** The query being shown, so its saved search row can look selected. */
    activeSearch?: string;
}

interface ContextMenuState {
    folderId: string;
    folderKind: string;
    isSystem: boolean;
    x: number;
    y: number;
}

/** null parentId key for the children index. */
const ROOT = "__root__";

/** "INBOX" → "Inbox" for system folders an IMAP server names in capitals. Display only. */
function displayName(f: Folder): string {
    const n = f.name;
    if (f.isSystem && n.length > 1 && n === n.toUpperCase() && /[A-Z]/.test(n)) {
        return n.charAt(0) + n.slice(1).toLowerCase();
    }
    return n;
}

export function FolderPane({ selectedFolderId, onSelectFolder, onCompose, onPrefetchFolder, onDropMessage, onOpenSearch, activeSearch }: FolderPaneProps) {
    const savedSearches = useSavedSearches();
    const navigate = useNavigate();
    const auth = useAuth();
    const accountEmail = auth.user?.profile.email ?? "Folders";

    // Track which folder the user is currently hovering during a drag,
    // so we can highlight just that one (not every folder).
    const [dropHoverId, setDropHoverId] = useState<string | null>(null);
    const folders = useFolders();
    const createFolder = useCreateFolder();
    const deleteFolder = useDeleteFolder();
    const markAllRead = useMarkAllRead();
    const emptyTrash = useEmptyTrash();

    // parentId of the folder the new-folder input is nested under; null =
    // top-level; undefined = input hidden.
    const [addingUnder, setAddingUnder] = useState<string | null | undefined>(undefined);
    const [newFolderName, setNewFolderName] = useState("");
    const newFolderInputRef = useRef<HTMLInputElement>(null);

    const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
    const [favouritesOpen, setFavouritesOpen] = useState(true);
    const [accountOpen, setAccountOpen] = useState(true);

    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
    const contextMenuRef = useRef<HTMLDivElement>(null);

    // Children index for tree rendering. Folders without parentId are roots —
    // the db/mock providers return a flat list which renders exactly as before.
    const childrenByParent = useMemo(() => {
        const map = new Map<string, Folder[]>();
        for (const f of folders.data ?? []) {
            const key = f.parentId ?? ROOT;
            const list = map.get(key);
            if (list) list.push(f);
            else map.set(key, [f]);
        }
        return map;
    }, [folders.data]);

    const favourites = useMemo(() => {
        const all = folders.data ?? [];
        return FAVOURITE_KINDS
            .map((kind) => all.find((f) => f.kind === kind))
            .filter((f): f is Folder => Boolean(f));
    }, [folders.data]);

    // Focus the new folder input when it appears
    useEffect(() => {
        if (addingUnder !== undefined) {
            setTimeout(() => newFolderInputRef.current?.focus(), 50);
        }
    }, [addingUnder]);

    // Dismiss context menu on outside click
    useEffect(() => {
        if (!contextMenu) return;
        const handler = (e: MouseEvent) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
                setContextMenu(null);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [contextMenu]);

    const confirmNewFolder = () => {
        const name = newFolderName.trim();
        if (name && addingUnder !== undefined) {
            createFolder.mutate({ name, parentId: addingUnder });
        }
        setAddingUnder(undefined);
        setNewFolderName("");
    };

    const handleNewFolderKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") { e.preventDefault(); confirmNewFolder(); }
        if (e.key === "Escape") { setAddingUnder(undefined); setNewFolderName(""); }
    };

    const openContextMenu = (e: React.MouseEvent, folder: Folder) => {
        e.preventDefault();
        setContextMenu({
            folderId: folder.id,
            folderKind: folder.kind,
            isSystem: folder.isSystem,
            x: e.clientX,
            y: e.clientY
        });
    };

    const toggleCollapsed = (id: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const newFolderInput = (
        <div className="mb-1 ml-4 flex items-center gap-1 pr-1">
            <input
                ref={newFolderInputRef}
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={handleNewFolderKeyDown}
                onBlur={confirmNewFolder}
                placeholder="Folder name"
                aria-label="New folder name"
                className="h-7 flex-1 rounded border border-[var(--mail-line-strong)] bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
        </div>
    );

    /** One folder row; `tree` adds the expander + children (the account section). */
    const renderFolder = (f: Folder, depth: number, tree: boolean, keyPrefix: string): React.ReactNode => {
        const Icon = iconByKind[f.kind] ?? FolderIcon;
        const isActive = f.id === selectedFolderId;
        const children = tree ? childrenByParent.get(f.id) ?? [] : [];
        const hasChildren = children.length > 0;
        const isCollapsed = collapsed.has(f.id);
        const name = displayName(f);
        // New-mail (unread) count shows on Inbox, Archive and custom folders.
        // Junk / Trash / Sent never show a count — new mail there isn't
        // actionable. Drafts / Flagged show a total-item count instead.
        const noBadge = f.kind === "junk" || f.kind === "trash" || f.kind === "sent";
        const useTotal = f.kind === "drafts" || f.kind === "flagged";
        const count = noBadge ? 0 : useTotal ? (f.totalCount ?? 0) : f.unreadCount;
        const badgeLabel = count > 0 ? String(count) : "";
        const ariaCount = useTotal
            ? (count > 0 ? `, ${count} item${count === 1 ? "" : "s"}` : "")
            : (count > 0 ? `, ${count} unread` : "");
        return (
            <div key={`${keyPrefix}${f.id}`}>
                <div className="flex items-center" style={{ paddingLeft: depth > 0 ? depth * 16 : 0 }}>
                    {hasChildren ? (
                        <button
                            type="button"
                            onClick={() => toggleCollapsed(f.id)}
                            aria-label={isCollapsed ? `Expand ${name}` : `Collapse ${name}`}
                            className="flex h-8 w-4 shrink-0 items-center justify-center rounded text-[var(--mail-text-3)] hover:text-foreground"
                        >
                            {isCollapsed
                                ? <ChevronRight className="h-3.5 w-3.5" />
                                : <ChevronDown className="h-3.5 w-3.5" />}
                        </button>
                    ) : (
                        <span className="w-4 shrink-0" aria-hidden />
                    )}
                    <button
                        type="button"
                        onClick={() => onSelectFolder(f.id)}
                        onMouseEnter={() => onPrefetchFolder?.(f.id)}
                        onFocus={() => onPrefetchFolder?.(f.id)}
                        onContextMenu={(e) => openContextMenu(e, f)}
                        onDragOver={(e) => {
                            if (!onDropMessage) return;
                            if (!e.dataTransfer.types.includes("application/x-fg-message")) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            if (dropHoverId !== f.id) setDropHoverId(f.id);
                        }}
                        onDragLeave={() => {
                            if (dropHoverId === f.id) setDropHoverId(null);
                        }}
                        onDrop={(e) => {
                            setDropHoverId(null);
                            if (!onDropMessage) return;
                            const raw = e.dataTransfer.getData("application/x-fg-message");
                            if (!raw) return;
                            try {
                                const payload = JSON.parse(raw) as {
                                    id: string;
                                    fromFolderId: string;
                                    ids?: string[];
                                };
                                if (payload.fromFolderId === f.id) return;
                                e.preventDefault();
                                const ids = payload.ids && payload.ids.length > 0
                                    ? payload.ids
                                    : [payload.id];
                                onDropMessage(ids, payload.fromFolderId, f.id);
                            } catch {
                                // ignored — malformed drag payload
                            }
                        }}
                        aria-current={isActive ? "page" : undefined}
                        aria-label={`${name}${ariaCount}`}
                        className={cn(
                            "flex h-8 w-full min-w-0 items-center gap-2.5 rounded pl-2.5 pr-2 text-sm text-foreground transition-colors",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                            dropHoverId === f.id && "ring-2 ring-primary",
                            isActive
                                ? "bg-[var(--mail-selected)] font-semibold"
                                : "hover:bg-[var(--mail-hover)]"
                        )}
                    >
                        <Icon className="h-4 w-4 shrink-0 text-foreground/70" aria-hidden />
                        <span className="flex-1 truncate text-left">{name}</span>
                        {badgeLabel ? (
                            // Unread as a bold accent number; item totals (Drafts,
                            // Flagged) in quiet grey — no pills.
                            <span
                                aria-hidden
                                className={cn(
                                    "shrink-0 text-xs tabular-nums",
                                    useTotal ? "font-normal text-[var(--mail-text-3)]" : "font-semibold text-primary"
                                )}
                            >
                                {badgeLabel}
                            </span>
                        ) : null}
                    </button>
                </div>
                {tree && addingUnder === f.id ? (
                    <div style={{ paddingLeft: (depth + 1) * 16 }}>{newFolderInput}</div>
                ) : null}
                {hasChildren && !isCollapsed
                    ? children.map((child) => renderFolder(child, depth + 1, true, keyPrefix))
                    : null}
            </div>
        );
    };

    return (
        <aside
            className="flex h-full w-[248px] shrink-0 flex-col border-r border-[var(--mail-line)] bg-[var(--mail-pane)]"
            aria-label="Folders and chats"
        >
            {/* One scrolling column, like Outlook: folders, then Tasks, then Chats. */}
            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4 pt-3">
                <div className="mx-1 mb-3 flex overflow-hidden rounded shadow-[var(--mail-shadow-4)]">
                    <button
                        type="button"
                        onClick={onCompose}
                        className="flex h-9 flex-1 items-center gap-2 bg-primary px-3.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-foreground"
                    >
                        <SquarePen className="h-4 w-4" aria-hidden />
                        New mail
                    </button>
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            aria-label="New item"
                            title="New item"
                            className="flex h-9 items-center border-l border-white/35 bg-primary px-2.5 text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-foreground"
                        >
                            <ChevronDown className="h-4 w-4" aria-hidden />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                            <DropdownMenuItem onSelect={onCompose}>
                                <SquarePen className="h-4 w-4" /> New mail
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => navigate("/calendar")}>
                                <CalendarPlus className="h-4 w-4" /> New event
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => navigate("/tasks")}>
                                <ListPlus className="h-4 w-4" /> New task
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <nav aria-label="Folders">
                    {favourites.length > 0 ? (
                        <section aria-label="Favourites" className="mb-1">
                            <button
                                type="button"
                                onClick={() => setFavouritesOpen((o) => !o)}
                                aria-expanded={favouritesOpen}
                                className={SECTION_HEADER}
                            >
                                {favouritesOpen
                                    ? <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                    : <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                                Favourites
                            </button>
                            {favouritesOpen ? favourites.map((f) => renderFolder(f, 0, false, "fav:")) : null}
                        </section>
                    ) : null}

                    {onOpenSearch && savedSearches.length > 0 ? (
                        <section aria-label="Saved searches" className="mb-1">
                            <div className={SECTION_HEADER}>Saved searches</div>
                            <ul>
                                {savedSearches.map((s) => (
                                    <li key={s.id} className="group/saved relative">
                                        <button
                                            type="button"
                                            onClick={() => onOpenSearch(s.query)}
                                            title={s.query}
                                            aria-current={activeSearch?.trim().toLowerCase() === s.query.toLowerCase() ? "true" : undefined}
                                            className={cn(
                                                "flex h-8 w-full items-center gap-2 rounded px-2 pr-7 text-left text-sm hover:bg-[var(--mail-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                                activeSearch?.trim().toLowerCase() === s.query.toLowerCase() && "bg-[var(--mail-selected)] font-medium"
                                            )}
                                        >
                                            <Search className="h-4 w-4 shrink-0 text-[var(--mail-text-2)]" aria-hidden />
                                            <span className="truncate">{s.name}</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => removeSavedSearch(s.id)}
                                            aria-label={`Remove saved search ${s.name}`}
                                            title="Remove"
                                            className="absolute right-1 top-1 hidden h-6 w-6 items-center justify-center rounded text-[var(--mail-text-3)] hover:bg-background hover:text-foreground focus-visible:flex group-hover/saved:flex"
                                        >
                                            <X className="h-3.5 w-3.5" aria-hidden />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : null}

                    <section aria-label={`Folders for ${accountEmail}`}>
                        <div className="flex items-center">
                            <button
                                type="button"
                                onClick={() => setAccountOpen((o) => !o)}
                                aria-expanded={accountOpen}
                                className={cn(SECTION_HEADER, "flex-1")}
                                title={accountEmail}
                            >
                                {accountOpen
                                    ? <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                    : <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                                <span className="truncate text-[13px] text-foreground">{accountEmail}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => void folders.refetch()}
                                disabled={folders.isFetching}
                                title="Refresh folders"
                                aria-label="Refresh folders"
                                className="mt-1.5 rounded p-1 text-[var(--mail-text-3)] transition-colors hover:bg-[var(--mail-hover)] hover:text-foreground disabled:opacity-60"
                            >
                                <RefreshCw className={cn("h-3.5 w-3.5", folders.isFetching && "animate-spin")} />
                            </button>
                            <button
                                type="button"
                                onClick={() => { setAccountOpen(true); setAddingUnder(null); setNewFolderName(""); }}
                                title="New folder"
                                aria-label="New folder"
                                className="mt-1.5 rounded p-1 text-[var(--mail-text-3)] transition-colors hover:bg-[var(--mail-hover)] hover:text-foreground"
                            >
                                <Plus className="h-3.5 w-3.5" />
                            </button>
                        </div>
                        {accountOpen ? (
                            <>
                                {addingUnder === null ? newFolderInput : null}
                                {folders.isPending ? (
                                    <div className="px-2 py-1.5 pl-[26px] text-xs text-muted-foreground">Loading…</div>
                                ) : folders.isError && !folders.data ? (
                                    // A failed background poll keeps the last good list —
                                    // only show the error when there's nothing to show.
                                    <div className="px-2 py-1.5 pl-[26px] text-xs text-destructive">{folders.error.message}</div>
                                ) : (
                                    (childrenByParent.get(ROOT) ?? []).map((f) => renderFolder(f, 0, true, ""))
                                )}
                            </>
                        ) : null}
                    </section>
                </nav>

                {/* To Do lists as drop targets: drag a message here to make it a task. */}
                <TaskDropTargets />
                <ChatRoster />
            </div>

            {/* Right-click context menu */}
            {contextMenu ? (
                <div
                    ref={contextMenuRef}
                    role="menu"
                    aria-label="Folder actions"
                    style={{ position: "fixed", top: contextMenu.y, left: contextMenu.x, zIndex: 9999 }}
                    className="min-w-[160px] rounded-md border bg-popover p-1 shadow-[var(--mail-shadow-28)]"
                >
                    <button
                        role="menuitem"
                        type="button"
                        className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                        onClick={() => {
                            markAllRead.mutate(contextMenu.folderId);
                            setContextMenu(null);
                        }}
                    >
                        Mark all as read
                    </button>
                    {contextMenu.folderKind !== "flagged" ? (
                        <button
                            role="menuitem"
                            type="button"
                            className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                            onClick={() => {
                                // Ensure the parent is expanded so the inline
                                // input is actually visible.
                                setCollapsed((prev) => {
                                    const next = new Set(prev);
                                    next.delete(contextMenu.folderId);
                                    return next;
                                });
                                setAccountOpen(true);
                                setAddingUnder(contextMenu.folderId);
                                setNewFolderName("");
                                setContextMenu(null);
                            }}
                        >
                            New subfolder
                        </button>
                    ) : null}
                    {!contextMenu.isSystem ? (
                        <button
                            role="menuitem"
                            type="button"
                            className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-accent"
                            onClick={() => {
                                if (window.confirm("Delete this folder and everything in it?")) {
                                    deleteFolder.mutate(contextMenu.folderId, {
                                        onSuccess: () => {
                                            if (selectedFolderId === contextMenu.folderId ||
                                                selectedFolderId.startsWith(contextMenu.folderId)) {
                                                onSelectFolder("inbox");
                                            }
                                        }
                                    });
                                }
                                setContextMenu(null);
                            }}
                        >
                            Delete folder
                        </button>
                    ) : null}
                    {contextMenu.folderKind === "trash" ? (
                        <button
                            role="menuitem"
                            type="button"
                            className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-accent"
                            onClick={() => {
                                emptyTrash.mutate();
                                setContextMenu(null);
                            }}
                        >
                            Empty trash
                        </button>
                    ) : null}
                </div>
            ) : null}
        </aside>
    );
}
